import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, Search, Printer, Building2, Trash2 } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function LaporanPPN() {
  const qc = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Ref for printing
  const printRef = useRef(null);
  const [printingSupplier, setPrintingSupplier] = useState(null);

  // Fetch purchasing requests
  const { data: requests = [], isLoading: isReqLoading } = useQuery({
    queryKey: ['purchasing-requests-all'],
    queryFn: async () => {
      try {
        const res = await base44.entities.PurchasingRequest?.list();
        return res || [];
      } catch (err) {
        console.error("Failed to fetch requests", err);
        return [];
      }
    }
  });

  // Fetch suppliers
  const { data: suppliers = [], isLoading: isSupLoading } = useQuery({
    queryKey: ['supplier-database'],
    queryFn: async () => {
      try {
        const res = await base44.entities.SupplierDatabase?.list();
        return res || [];
      } catch (err) {
        return [];
      }
    }
  });

  // Flat Filtered Data
  const flatFilteredRequests = useMemo(() => {
    // 1. Filter only items with PPN > 0 (or ppn_status === 'exclude')
    let filtered = requests.filter(req => req.ppn_nominal > 0 || req.ppn_status === 'exclude');

    // 2. Date Range Filter
    if (startDate && endDate) {
      const s = startOfDay(new Date(startDate));
      const e = endOfDay(new Date(endDate));
      filtered = filtered.filter(req => {
        if (!req.created_date) return false;
        const d = parseISO(req.created_date);
        return isWithinInterval(d, { start: s, end: e });
      });
    }

    // 3. Search Filter
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(req => 
        (req.nama_sales || '').toLowerCase().includes(q) ||
        (req.nama_item || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [requests, startDate, endDate, searchTerm]);

  // Batch Selection
  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    selectedCount,
    startSelection
  } = useBatchSelection(flatFilteredRequests, req => req.id);

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await base44.entities.PurchasingRequest.delete(id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchasing-requests-all'] });
      clearSelection();
    }
  });

  // Filter and Group Data
  const groupedData = useMemo(() => {
    // 4. Group by Supplier (nama_sales)
    const groups = {};
    flatFilteredRequests.forEach(req => {
      const supName = req.nama_sales || 'Tanpa Nama Supplier';
      if (!groups[supName]) {
        // Find supplier details if available
        const supInfo = suppliers.find(s => s.nama_sales === req.nama_sales) || {};
        groups[supName] = {
          supplierName: supName,
          supplierInfo: supInfo,
          items: [],
          totalBarang: 0,
          totalPPN: 0,
          grandTotal: 0
        };
      }
      
      const subtotalBarang = req.total_barang || (req.qty_beli * req.harga_unit) || 0;
      const ppnNominal = req.ppn_nominal || (subtotalBarang * (req.ppn_percentage || 11) / 100);

      groups[supName].items.push({
        ...req,
        calculatedSubtotal: subtotalBarang,
        calculatedPPN: ppnNominal
      });
      
      groups[supName].totalBarang += subtotalBarang;
      groups[supName].totalPPN += ppnNominal;
      groups[supName].grandTotal += (subtotalBarang + ppnNominal);
    });

    // Convert object to array and sort by supplier name
    return Object.values(groups).sort((a, b) => a.supplierName.localeCompare(b.supplierName));
  }, [flatFilteredRequests, suppliers]);

  // Handle Print
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Faktur_Pajak_${(printingSupplier?.supplierName || printingSupplier?.clientName || '').replace(/\s+/g, '_')}`,
    onAfterPrint: () => setPrintingSupplier(null)
  });

  const triggerPrint = (group) => {
    setPrintingSupplier(group);
    setTimeout(() => {
      handlePrint();
    }, 100); // Give React time to render the print view
  };

  const [activeTab, setActiveTab] = useState('keluaran'); // 'keluaran' | 'masuk'

  // Fetch Client POs (Sales)
  const { data: clientPOs = [], isLoading: isPoLoading } = useQuery({
    queryKey: ['client-pos-all'],
    queryFn: async () => {
      try {
        return await base44.entities.ClientPO.list() || [];
      } catch (err) {
        return [];
      }
    }
  });

  // Flat Filtered Sales (PPN Keluaran)
  const flatFilteredSales = useMemo(() => {
    let filtered = clientPOs.filter(po => po.ppn_mode === 'exclude');

    if (startDate && endDate) {
      const s = startOfDay(new Date(startDate));
      const e = endOfDay(new Date(endDate));
      filtered = filtered.filter(po => {
        const dStr = po.date || po.created_at?.split('T')[0] || po.tanggal_invoice || po.rawRecord?.created_at;
        if (!dStr) return false;
        try {
          const d = new Date(dStr);
          return isWithinInterval(d, { start: s, end: e });
        } catch { return false; }
      });
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(po => 
        (po.customerName || '').toLowerCase().includes(q) ||
        (po.id || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [clientPOs, startDate, endDate, searchTerm]);

  // Grouped Sales Data
  const groupedSales = useMemo(() => {
    const groups = {};
    flatFilteredSales.forEach(po => {
      const cName = po.customerName || 'Client Tanpa Nama';
      if (!groups[cName]) {
        groups[cName] = {
          clientName: cName,
          clientInfo: { address: po.customerAddress, npwp: po.npwp },
          items: [],
          totalBarang: 0,
          totalPPN: 0,
          grandTotal: 0
        };
      }
      
      const prodJual = (po.products || po.items || []).reduce((s, p) => s + (p.total_harga_jual || p.subtotal || p.hargaTotal || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + ((l.harga_jual || 0) * (l.qty_variant || 1)), 0);
      const totalJualSemua = prodJual + legJual;
      const nominalPPN = (totalJualSemua * (parseFloat(po.ppn_percentage) || 0)) / 100;

      groups[cName].items.push({
        ...po,
        calculatedSubtotal: totalJualSemua,
        calculatedPPN: nominalPPN
      });
      
      groups[cName].totalBarang += totalJualSemua;
      groups[cName].totalPPN += nominalPPN;
      groups[cName].grandTotal += (totalJualSemua + nominalPPN);
    });
    return Object.values(groups).sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [flatFilteredSales]);


  return (
    <div className="space-y-6 text-[#302621] print:hidden">
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={activeTab === 'masuk' ? flatFilteredRequests.length : flatFilteredSales.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={() => {
          if (confirm(`Yakin ingin menghapus ${selectedCount} data PPN terpilih?`)) {
            batchDeleteMutation.mutate(Array.from(selectedIds));
          }
        }}
        isDeleting={batchDeleteMutation.isPending}
      />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-black tracking-tight text-slate-800">Laporan PPN (Pajak)</h1>
          <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1">
            Rekap Pajak Keluaran (Penjualan Klien) dan Masukan (Pembelian Supplier).
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <Button 
          variant={activeTab === 'keluaran' ? 'default' : 'outline'} 
          className={activeTab === 'keluaran' ? 'bg-[#302621] text-white' : ''}
          onClick={() => setActiveTab('keluaran')}
        >
          PPN Keluaran (Penjualan)
        </Button>
        <Button 
          variant={activeTab === 'masuk' ? 'default' : 'outline'}
          className={activeTab === 'masuk' ? 'bg-[#302621] text-white' : ''}
          onClick={() => setActiveTab('masuk')}
        >
          PPN Masukan (Pembelian)
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-100 flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cari nama supplier atau item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Calendar className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <CustomDatePicker
                value={startDate}
                onChange={setStartDate}
                className="pl-8 h-9 text-xs w-[140px]"
              />
            </div>
            <span className="text-slate-400 text-xs">-</span>
            <div className="relative">
              <Calendar className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <CustomDatePicker
                value={endDate}
                onChange={setEndDate}
                className="pl-8 h-9 text-xs w-[140px]"
              />
            </div>
            {(startDate || endDate) && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-9 text-xs text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                onClick={() => { setStartDate(''); setEndDate(''); }}
              >
                Reset
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {(isReqLoading || isSupLoading || isPoLoading) ? (
            <div className="text-center py-12 text-sm text-slate-400">Memuat data PPN...</div>
          ) : (activeTab === 'masuk' ? groupedData : groupedSales).length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-400">Tidak ada transaksi yang dikenakan PPN.</div>
          ) : (
            <div className="space-y-6">
              {(activeTab === 'masuk' ? groupedData : groupedSales).map((group, idx) => (
                <Card key={idx} className={`border ${group.items.some(i => selectedIds.has(i.id)) ? 'border-amber-400 shadow-md' : 'border-[#E6D5B8]/80 shadow-xs'} overflow-hidden transition-all`}>
                  <div className="bg-[#FFFDF9] border-b border-[#E6D5B8]/50 p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 border border-amber-200">
                        <Building2 className="w-5 h-5 text-amber-700" />
                      </div>
                      <div>
                        <h2 className="font-black text-slate-800 text-sm md:text-lg uppercase tracking-tight">{group.supplierName || group.clientName}</h2>
                        <p className="text-[9px] md:text-xs text-slate-500 mt-0.5">
                          {group.items.length} Item Transaksi {activeTab === 'masuk' ? `• ${group.supplierInfo?.bank ? `${group.supplierInfo.bank} - ${group.supplierInfo.no_rekening}` : 'Bank tidak tercatat'}` : ''}
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => triggerPrint(group)}
                      className="bg-slate-900 hover:bg-slate-800 text-white gap-2 text-xs font-bold shrink-0"
                    >
                      <Printer className="w-4 h-4" /> Cetak Faktur Pajak
                    </Button>
                  </div>

                  <div className="p-0 overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-50/80 text-slate-500 border-b border-slate-100 uppercase tracking-wider font-bold text-[10px]">
                        <tr>
                          {isSelectionMode && <th className="px-4 py-3 w-10"></th>}
                          <th className="px-4 py-3">Tanggal</th>
                          <th className="px-4 py-3">Item {activeTab === 'masuk' ? 'Pembelian' : 'Penjualan'}</th>
                          <th className="px-4 py-3 text-right">Harga {activeTab === 'masuk' ? 'Barang' : 'Invoice'}</th>
                          <th className="px-4 py-3 text-right">PPN</th>
                          <th className="px-4 py-3 text-right">Total</th>
                          {!isSelectionMode && activeTab === 'masuk' && <th className="px-4 py-3 w-10"></th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {group.items.map((item, i) => {
                          const isSelected = selectedIds.has(item.id);
                          return (
                            <tr 
                              key={i} 
                              className={`transition-colors ${isSelectionMode ? 'cursor-pointer hover:bg-slate-50' : 'hover:bg-slate-50/50'} ${isSelected ? 'bg-amber-50/50' : ''}`}
                              onClick={() => isSelectionMode && toggleSelection(item.id)}
                            >
                              {isSelectionMode && (
                                <td className="px-4 py-3">
                                  <Checkbox checked={isSelected} onCheckedChange={() => toggleSelection(item.id)} />
                                </td>
                              )}
                              <td className="px-4 py-3 text-slate-500">
                                {(item.created_date || item.tanggal_invoice || item.created_at || item.rawRecord?.created_at) ? format(parseISO(item.created_date || item.tanggal_invoice || item.created_at || item.rawRecord?.created_at), 'dd MMM yyyy') : '-'}
                              </td>
                              <td className="px-4 py-3">
                                {item.id?.startsWith('INV-') ? (
                                  <a 
                                    href={`/office/finance/invoices?search=${item.id}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="font-bold text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {item.nama_item || item.id}
                                  </a>
                                ) : (
                                  <p className="font-bold text-slate-700">{item.nama_item || item.id}</p>
                                )}
                                <p className="text-[10px] text-slate-400 mt-0.5">{item.qty_beli || '-'} × Rp {fmt(item.harga_unit || item.calculatedSubtotal)}</p>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-slate-600">
                                Rp {fmt(item.calculatedSubtotal)}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 py-0 px-1.5 font-bold">
                                  + Rp {fmt(item.calculatedPPN)}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-slate-800">
                                Rp {fmt(item.calculatedSubtotal + item.calculatedPPN)}
                              </td>
                              {!isSelectionMode && activeTab === 'masuk' && (
                                <td className="px-4 py-3 text-right">
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="w-8 h-8 text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                                    onClick={(e) => { e.stopPropagation(); startSelection(item.id); }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-50/50 font-bold border-t-2 border-slate-200">
                        <tr>
                          <td colSpan={isSelectionMode ? 3 : 2} className="px-4 py-3 text-right text-slate-500 uppercase text-[10px] tracking-wider">Total {group.supplierName || group.clientName}</td>
                          <td className="px-4 py-3 text-right text-slate-700">Rp {fmt(group.totalBarang)}</td>
                          <td className="px-4 py-3 text-right text-amber-700">Rp {fmt(group.totalPPN)}</td>
                          <td className="px-4 py-3 text-right text-[#8C6208] text-sm font-black">Rp {fmt(group.grandTotal)}</td>
                          {!isSelectionMode && activeTab === 'masuk' && <td></td>}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Hidden Print Area */}
      <div className="hidden">
        <div ref={printRef} className="p-8 font-sans text-black" style={{ width: '210mm', minHeight: '297mm' }}>
          {printingSupplier && (
            <div className="max-w-full">
              <div className="text-center mb-8 border-b-2 border-black pb-4">
                <h1 className="text-2xl font-black uppercase tracking-widest mb-1">Faktur Pajak</h1>
                <p className="text-sm">Berdasarkan Peraturan Direktur Jenderal Pajak</p>
              </div>

              <div className="flex justify-between text-sm mb-6">
                <div>
                  <p className="font-bold">Kode dan Nomor Seri Faktur Pajak :</p>
                  <p className="mt-1 border-b border-black w-64">&nbsp;</p>
                </div>
              </div>

              {/* Pengusaha Kena Pajak (Seller) */}
              <div className="border border-black mb-4">
                <div className="bg-gray-100 font-bold border-b border-black p-1.5 text-xs">PENGUSAHA KENA PAJAK</div>
                <div className="p-2 text-xs space-y-1">
                  {printingSupplier.supplierName ? (
                    <>
                      <div className="flex"><div className="w-32">Nama</div><div>: <span className="font-bold uppercase">{printingSupplier.supplierName}</span></div></div>
                      <div className="flex"><div className="w-32">Alamat</div><div>: {printingSupplier.supplierInfo?.alamat || '.......................................................................'}</div></div>
                      <div className="flex"><div className="w-32">NPWP</div><div>: {printingSupplier.supplierInfo?.npwp || '.......................................................................'}</div></div>
                    </>
                  ) : (
                    <>
                      <div className="flex"><div className="w-32">Nama</div><div>: <span className="font-bold uppercase">PT. SATALA DERMATECH ESSENTIAL</span></div></div>
                      <div className="flex"><div className="w-32">Alamat</div><div>: Grogol, Sukoharjo, Jawa Tengah</div></div>
                      <div className="flex"><div className="w-32">NPWP</div><div>: .......................................................................</div></div>
                    </>
                  )}
                </div>
              </div>

              {/* Pembeli Barang Kena Pajak (Buyer) */}
              <div className="border border-black mb-6">
                <div className="bg-gray-100 font-bold border-b border-black p-1.5 text-xs">PEMBELI BARANG KENA PAJAK / PENERIMA JASA KENA PAJAK</div>
                <div className="p-2 text-xs space-y-1">
                  {printingSupplier.supplierName ? (
                    <>
                      <div className="flex"><div className="w-32">Nama</div><div>: <span className="font-bold uppercase">PT. SATALA DERMATECH ESSENTIAL</span></div></div>
                      <div className="flex"><div className="w-32">Alamat</div><div>: Grogol, Sukoharjo, Jawa Tengah</div></div>
                      <div className="flex"><div className="w-32">NPWP</div><div>: .......................................................................</div></div>
                    </>
                  ) : (
                    <>
                      <div className="flex"><div className="w-32">Nama</div><div>: <span className="font-bold uppercase">{printingSupplier.clientName}</span></div></div>
                      <div className="flex"><div className="w-32">Alamat</div><div>: {printingSupplier.clientInfo?.address || '.......................................................................'}</div></div>
                      <div className="flex"><div className="w-32">NPWP</div><div>: {printingSupplier.clientInfo?.npwp || '.......................................................................'}</div></div>
                    </>
                  )}
                </div>
              </div>

              {/* Tabel Barang */}
              <table className="w-full text-xs border-collapse border border-black mb-4">
                <thead>
                  <tr className="border-b border-black text-center">
                    <th className="border-r border-black p-2 w-10">No.</th>
                    <th className="border-r border-black p-2">Nama Barang Kena Pajak / Jasa Kena Pajak</th>
                    <th className="p-2 w-32">Harga Jual / Penggantian / Uang Muka / Termin (Rp)</th>
                  </tr>
                </thead>
                <tbody>
                  {printingSupplier.items.map((item, i) => (
                    <tr key={i} className="border-b border-black">
                      <td className="border-r border-black p-2 text-center align-top">{i + 1}</td>
                      <td className="border-r border-black p-2 align-top">
                        <p className="font-semibold">{item.nama_item || item.id}</p>
                        <p className="text-[10px] text-gray-500 mt-1">Qty: {item.qty_beli || 1} x {fmt(item.harga_unit || item.calculatedSubtotal)}</p>
                      </td>
                      <td className="p-2 text-right align-bottom font-medium">
                        {fmt(item.calculatedSubtotal)}
                      </td>
                    </tr>
                  ))}
                  {/* Empty rows filler if needed, skipped for brevity */}
                </tbody>
                <tfoot>
                  <tr className="border-t border-black font-bold">
                    <td colSpan={2} className="border-r border-black p-2 text-left">Harga Jual / Penggantian / Uang Muka / Termin **)</td>
                    <td className="p-2 text-right">{fmt(printingSupplier.totalBarang)}</td>
                  </tr>
                  <tr className="border-t border-black font-bold">
                    <td colSpan={2} className="border-r border-black p-2 text-left">Dikurangi Potongan Harga</td>
                    <td className="p-2 text-right">0</td>
                  </tr>
                  <tr className="border-t border-black font-bold">
                    <td colSpan={2} className="border-r border-black p-2 text-left">Dikurangi Uang Muka yang telah diterima</td>
                    <td className="p-2 text-right">0</td>
                  </tr>
                  <tr className="border-t border-black font-bold">
                    <td colSpan={2} className="border-r border-black p-2 text-left">Dasar Pengenaan Pajak</td>
                    <td className="p-2 text-right">{fmt(printingSupplier.totalBarang)}</td>
                  </tr>
                  <tr className="border-t border-black font-bold">
                    <td colSpan={2} className="border-r border-black p-2 text-left">PPN = 11% x Dasar Pengenaan Pajak</td>
                    <td className="p-2 text-right">{fmt(printingSupplier.totalPPN)}</td>
                  </tr>
                  <tr className="border-t border-black font-bold">
                    <td colSpan={2} className="border-r border-black p-2 text-left bg-gray-100">TOTAL</td>
                    <td className="p-2 text-right bg-gray-100">{fmt(printingSupplier.grandTotal)}</td>
                  </tr>
                </tfoot>
              </table>

              <div className="flex justify-between text-xs mt-8">
                <div className="w-1/2">
                  <p className="italic">*) Coret yang tidak perlu</p>
                </div>
                <div className="w-1/2 flex flex-col items-end">
                  <p>Sukoharjo, {format(new Date(), 'dd MMMM yyyy', { locale: idLocale })}</p>
                  <p className="mb-20 mt-1">{printingSupplier.supplierName || 'PT. SATALA DERMATECH ESSENTIAL'}</p>
                  <div className="border-b border-black w-48 mb-1">&nbsp;</div>
                  <p className="text-[10px]">Nama Lengkap / Cap Perusahaan</p>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
